import React, { useEffect, useState, useRef, useCallback } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import BarLoader from "react-spinners/BarLoader";
// import TableContainer from "@material-ui/core/TableContainer";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import Pagination from "@mui/material/Pagination";
import { TextInput } from "hesus-ui-kit";

import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
  useFilters,
} from "react-table";
import isEqual from "lodash/isEqual";
import TableToolbar from "./TableToolbar";
import DisplayCell from "./DisplayCell";
import RequestStateInfoList from "../../RequestStateInfoList";

import {
  TABLE_REQUEST_LIST_TO_BE_PROCESSED,
  TABLE_REQUEST_LIST_PENDING,
  TABLE_SOLUTIONS_LIST,
  TABLE_COMPANY_SOLUTIONS_LIST,
  TABLE_SELLING_PRICES_LIST,
  TABLE_TRANSPORT_QUOTATIONS,
  TABLE_ANALYTICAL_NOTES_LIST,
  TABLE_JOBSITE_SELLING_PRICES_LIST,
  TABLE_MEETING_REPORTS_LIST,
  TABLE_MEETING_REPORTS_LIST_WITH_BUTTON,
  TABLE_CONTACTS_LIST,
  TABLE_COMPANY_LIST,
  TABLE_SITES_LIST,
  TABLE_SELLING_PRICE_LIST,
  TABLE_ENQUIRY_LIST,
  TABLE_PAYMENT_TERMS_LIST,
  TABLE_PURCHASE_ORDER_LIST,
  TABLE_VAT_LIST,
  TABLE_BANK_ACCOUNT_LIST,
  TABLE_ADMIN_LIST,
  TABLE_MENTION_GROUP_LIST,
  TABLE_SPECIFIC_CONDITIONS_LIST,
  TABLE_QUOTES_LIST,
  TABLE_TRANSPORT_LIST,
  TABLE_PRODUCTS_LIST,
  TABLE_LEADS_LIST,
  TABLE_MAIL_TOKEN_LIST,
  TABLE_USER_LIST,
  TABLE_GENERAL_PARAMETERS_LIST,
} from "@utils/constants";
import { getRequestFieldName } from "@utils/utils";

const BaseTable = ({
  columns,
  data,
  updateMyData,
  skipPageReset,
  manualPagination,
  pageCount: controlledPageCount,
  fetchData,
  loading,
  type,
  renderExtraFilters,
  initialPageSize,
  defaultSortOptions,
  canRefresh,
  t,
  currentPageToFirst,
  setCurrentPageToFirst,
  createNewAction,
  statusKey,
  statusCheck,
  customAction,
  extraData,
}) => {
  const defaultColumn = React.useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: function DefaultColumnFilter() {
        return null;
      },
      Cell: DisplayCell,
    }),
    []
  );
  const {
    getTableProps,
    headerGroups,
    prepareRow,
    page,
    gotoPage,
    pageCount,
    setPageSize,
    setGlobalFilter,
    state: { pageSize, globalFilter, sortBy, filters },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: initialPageSize,
        sortBy: defaultSortOptions,
      },
      defaultColumn,
      autoResetPage: !skipPageReset,
      // updateMyData isn't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData,
      manualPagination,
      pageCount: controlledPageCount,
      autoResetGlobalFilter: false,
      manualSortBy: true,
      manualGlobalFilter: true,
      disableMultiSort: true,
      autoResetSortBy: false,
      manualFilters: true,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const checkAccessor = () => {
    let columnName = 0;
    if (
      type === TABLE_SOLUTIONS_LIST ||
      type === TABLE_COMPANY_SOLUTIONS_LIST ||
      type === TABLE_TRANSPORT_QUOTATIONS ||
      type === TABLE_SELLING_PRICES_LIST ||
      type === TABLE_ANALYTICAL_NOTES_LIST ||
      type === TABLE_JOBSITE_SELLING_PRICES_LIST ||
      type === TABLE_MEETING_REPORTS_LIST ||
      type === TABLE_MEETING_REPORTS_LIST_WITH_BUTTON ||
      type === TABLE_CONTACTS_LIST ||
      type === TABLE_COMPANY_LIST ||
      type === TABLE_SITES_LIST ||
      type === TABLE_SELLING_PRICE_LIST ||
      type === TABLE_ENQUIRY_LIST ||
      type === TABLE_PAYMENT_TERMS_LIST ||
      type === TABLE_PURCHASE_ORDER_LIST ||
      type === TABLE_VAT_LIST ||
      type === TABLE_BANK_ACCOUNT_LIST ||
      type === TABLE_ADMIN_LIST ||
      type === TABLE_MENTION_GROUP_LIST ||
      type === TABLE_SPECIFIC_CONDITIONS_LIST ||
      type === TABLE_QUOTES_LIST ||
      type === TABLE_TRANSPORT_LIST ||
      type === TABLE_PRODUCTS_LIST ||
      type === TABLE_LEADS_LIST ||
      type === TABLE_MAIL_TOKEN_LIST ||
      type === TABLE_USER_LIST ||
      type === TABLE_GENERAL_PARAMETERS_LIST
    ) {
      columnName = defaultSortOptions[0].id;
    } else {
      columnName =
        columns.length > 0
          ? columns.findIndex(
              (column) => column.accessor === defaultSortOptions[0].id
            )
          : 0;
    }
    return columnName;
  };
  const [pageStart, setPageStart] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [currentRequestParams, setRequestParams] = useState({});
  const [isRefreshDataAgain, setDataRefreshStatus] = useState(false);
  const [sort, setSortOption] = useState({
    column: checkAccessor(),
    dir: defaultSortOptions[0].dir,
  });

  // Local input states for debounced inputs
  const [pageSizeInput, setPageSizeInput] = useState(initialPageSize);
  const [goToPageInput, setGoToPageInput] = useState(1);
  const pageSizeDebounceRef = useRef(null);
  const goToPageDebounceRef = useRef(null);
  const DEBOUNCE_DELAY = 500; // 500ms delay

  /**
   * Effect to apply filters.
   */
  useEffect(() => {
    let appliedFiltersList = [...appliedFilters];
    for (const columnFilter of filters) {
      const fieldName = getRequestFieldName(columnFilter.id);
      // To remove previous filter value
      appliedFiltersList = appliedFiltersList.filter(
        (selectedFilter) => selectedFilter.column !== fieldName
      );
      if (Array.isArray(columnFilter.value)) {
        if (fieldName === "closeDate") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.join(","),
          });
        } else if (fieldName === "stage") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "country") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "code") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "category") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "transportTypes") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "subCategory") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else if (fieldName === "type") {
          appliedFiltersList.push({
            column: fieldName,
            value: columnFilter.value.map((item) => item.value),
          });
        } else {
          for (const filterItem of columnFilter.value) {
            appliedFiltersList.push({
              column: fieldName,
              value: filterItem.value,
            });
          }
        }
      } else {
        appliedFiltersList.push({
          column: fieldName,
          value: columnFilter.value,
        });
      }
    }

    if (filters.length > 0) {
      setAppliedFilters(appliedFiltersList);
    } else {
      setAppliedFilters([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, globalFilter, filters]);
  /**
   * Effect to apply sort options.
   */
  useEffect(() => {
    if (columns.length > 0) {
      if (
        type === TABLE_SOLUTIONS_LIST ||
        type === TABLE_COMPANY_SOLUTIONS_LIST ||
        type === TABLE_TRANSPORT_QUOTATIONS ||
        type === TABLE_SELLING_PRICES_LIST ||
        type === TABLE_ANALYTICAL_NOTES_LIST ||
        type === TABLE_JOBSITE_SELLING_PRICES_LIST ||
        type === TABLE_MEETING_REPORTS_LIST ||
        type === TABLE_MEETING_REPORTS_LIST_WITH_BUTTON ||
        type === TABLE_CONTACTS_LIST ||
        type === TABLE_COMPANY_LIST ||
        type === TABLE_SITES_LIST ||
        type === TABLE_SELLING_PRICE_LIST ||
        type === TABLE_ENQUIRY_LIST ||
        type === TABLE_PAYMENT_TERMS_LIST ||
        type === TABLE_PURCHASE_ORDER_LIST ||
        type === TABLE_VAT_LIST ||
        type === TABLE_BANK_ACCOUNT_LIST ||
        type === TABLE_ADMIN_LIST ||
        type === TABLE_MENTION_GROUP_LIST ||
        type === TABLE_SPECIFIC_CONDITIONS_LIST ||
        type === TABLE_QUOTES_LIST ||
        type === TABLE_TRANSPORT_LIST ||
        type === TABLE_PRODUCTS_LIST ||
        type === TABLE_LEADS_LIST ||
        type === TABLE_MAIL_TOKEN_LIST ||
        type === TABLE_USER_LIST ||
        type === TABLE_GENERAL_PARAMETERS_LIST
      ) {
        let sortLocal = {
          column: defaultSortOptions[0].id,
          dir: defaultSortOptions[0].dir,
        };

        if (sortBy.length > 0) {
          const selectedColumn = sortBy[0].id;
          if (selectedColumn) {
            sortLocal = {
              column: selectedColumn,
              dir: sortBy[0].desc ? "desc" : "asc",
            };
          }
        }
        setSortOption(sortLocal);
      } else {
        let sortLocal = {
          column: columns.findIndex(
            (column) => column.accessor === defaultSortOptions[0].id
          ),
          dir: defaultSortOptions[0].dir,
        };

        if (sortBy.length > 0) {
          const selectedColumnIndex = columns.findIndex(
            (column) => column.accessor === sortBy[0].id
          );
          if (selectedColumnIndex >= 0) {
            sortLocal = {
              column: selectedColumnIndex,
              dir: sortBy[0].desc ? "desc" : "asc",
            };
          }
        }

        setSortOption(sortLocal);
      }
    }
  }, [sortBy, type, columns, defaultSortOptions]);

  useEffect(() => {
    const requestParams = {
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
    };

    if (
      canRefresh ||
      (!loading && !isEqual(requestParams, currentRequestParams))
    ) {
      setDataRefreshStatus(false);
      fetchData(requestParams);
      setRequestParams(requestParams);
    } else if (loading && !isEqual(requestParams, currentRequestParams)) {
      setDataRefreshStatus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canRefresh,
    currentRequestParams,
    sort,
    currentPage,
    pageStart,
    globalFilter,
    appliedFilters,
    pageSize,
  ]);

  /**
   * Run this effect if filters are applied while data is being fetched
   */
  useEffect(() => {
    if (!loading && isRefreshDataAgain) {
      const requestParams = {
        pageSize,
        pageStart,
        globalFilter,
        sort,
        appliedFilters,
        currentPage,
      };
      fetchData(requestParams);
      setRequestParams(requestParams);
      setDataRefreshStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isRefreshDataAgain]);

  const switchPage = (selectedPage) => {
    let pageStartLocal;
    if (selectedPage === 1) {
      pageStartLocal = 0;
    } else {
      pageStartLocal = pageSize * selectedPage - pageSize;
    }

    setCurrentPage(selectedPage);
    setPageStart(pageStartLocal);
    // if (!loading) {
    //   fetchData({
    //     pageSize,
    //     pageStart: pageStartLocal,
    //     globalFilter,
    //     sort,
    //     appliedFilters,
    //     currentPage: selectedPage,
    //   });
    // }
    gotoPage(selectedPage);
  };

  useEffect(() => {
    switchPage(1);
    setCurrentPageToFirst(false);
  }, [currentPageToFirst]);

  const handleChangePage = (event, newPage) => {
    switchPage(newPage);
    setGoToPageInput(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    setPageSizeInput(value);

    // Clear previous timeout
    if (pageSizeDebounceRef.current) {
      clearTimeout(pageSizeDebounceRef.current);
    }

    // Set new timeout to apply change after delay
    pageSizeDebounceRef.current = setTimeout(() => {
      const newPageSize = Number(value);
      if (newPageSize > 0) {
        setPageSize(newPageSize);
        setCurrentPage(1);
        setPageStart(0);
        setGoToPageInput(1);
      }
    }, DEBOUNCE_DELAY);
  };

  const goToPage = (e) => {
    const value = e.target.value;
    setGoToPageInput(value);

    // Clear previous timeout
    if (goToPageDebounceRef.current) {
      clearTimeout(goToPageDebounceRef.current);
    }

    // Set new timeout to apply change after delay
    goToPageDebounceRef.current = setTimeout(() => {
      const pageNumber = Number(value);
      if (pageNumber > 0 && pageNumber <= Math.ceil(pageCount / pageSize)) {
        switchPage(pageNumber);
      }
    }, DEBOUNCE_DELAY);
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (pageSizeDebounceRef.current)
        clearTimeout(pageSizeDebounceRef.current);
      if (goToPageDebounceRef.current)
        clearTimeout(goToPageDebounceRef.current);
    };
  }, []);

  return (
    <TableContainer className="table-container">
      <div
        className={
          type !== TABLE_REQUEST_LIST_TO_BE_PROCESSED &&
          type !== TABLE_REQUEST_LIST_PENDING
            ? "table__filters-container table__filters-container--margin-bottom"
            : "table__filters-container"
        }
      >
        <div className="table__filters-wrapper">
          {renderExtraFilters()}
          {headerGroups.map((headerGroup, headerGroupsIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`headerGroups${headerGroupsIndex}`}>
              {headerGroup.headers.map((column) => (
                <React.Fragment key={column.render("Header")}>
                  {column.canFilter ? <>{column.render("Filter")}</> : null}
                </React.Fragment>
              ))}
            </React.Fragment>
          ))}
        </div>
        <TableToolbar
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
          type={type}
          createNewAction={createNewAction}
          customAction={customAction}
          extraData={extraData}
        />
      </div>
      {type === TABLE_REQUEST_LIST_PENDING && <RequestStateInfoList type={2} />}
      {type === TABLE_REQUEST_LIST_TO_BE_PROCESSED && (
        <RequestStateInfoList type={1} />
      )}
      {loading && <BarLoader width="100%" color="#1bace4" />}
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <Table {...getTableProps()} className="list-table">
        <TableHead>
          {headerGroups.map((headerGroup, headerGroupIndex) => (
            <TableRow
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...headerGroup.getHeaderGroupProps()}
              // eslint-disable-next-line react/no-array-index-key
              key={`headerGroup${headerGroupIndex}`}
            >
              {headerGroup.headers.map((column, headerIndex) => (
                <TableCell
                  // eslint-disable-next-line react/no-array-index-key
                  key={`table-header-cell${headerIndex}`}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...(column.id === "selection"
                    ? column.getHeaderProps()
                    : column.getHeaderProps(column.getSortByToggleProps()))}
                >
                  {column.render("Header")}
                  {column.id !== "selection" ? (
                    <TableSortLabel
                      active={column.isSorted}
                      // react-table has a unsorted state which is not treated here
                      direction={column.isSortedDesc ? "desc" : "asc"}
                    />
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {page.map((row, i) => {
            prepareRow(row);
            return (
              // eslint-disable-next-line
              <TableRow
                {...row.getRowProps()}
                key={`tableRow${i}`}
                style={row?.original?.rowStyle}
              >
                {row.cells.map((cell, cellIndex) => {
                  return (
                    <TableCell
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...cell.getCellProps()}
                      // eslint-disable-next-line react/no-array-index-key
                      key={`tableCell${cellIndex}`}
                    >
                      <div>{cell.render("Cell")}</div>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {page.length === 0 && !loading && (
            <tr>
              <td
                colSpan={columns.length}
                className="list-table__no-records-message"
              >
                No records found
              </td>
            </tr>
          )}
        </TableBody>
      </Table>
      {pageCount > pageSize && (
        <div className="table-pagination__container">
          <TextInput
            type="text"
            id="pagesize"
            label={t("GENERAL_LIST_SHOW")}
            inputClass="table-pagination__page-size-input"
            value={pageSizeInput}
            placeholder=""
            onChange={handleChangeRowsPerPage}
            containerClass="table-pagination__page-size-wrapper"
            labelClass="table-pagination__page-size-label"
          />
          <TextInput
            type="text"
            id="gotopage"
            label={t("GENERAL_LIST_GO_TO_PAGE")}
            inputClass="table-pagination__go-to-page-input"
            value={goToPageInput}
            placeholder=""
            onChange={goToPage}
            containerClass="table-pagination__go-to-page-wrapper"
            labelClass="table-pagination__go-to-page-label"
          />

          <Pagination
            count={Math.ceil(pageCount / pageSize)}
            showFirstButton
            showLastButton
            defaultPage={1}
            page={currentPage}
            onChange={handleChangePage}
          />
        </div>
      )}
    </TableContainer>
  );
};

BaseTable.propTypes = {
  canRefresh: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.string.isRequired,
  pageCount: PropTypes.number.isRequired,
  manualPagination: PropTypes.bool.isRequired,
  updateMyData: PropTypes.func.isRequired,
  setData: PropTypes.func.isRequired,
  skipPageReset: PropTypes.bool.isRequired,
  defaultSortOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  initialPageSize: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  fetchData: PropTypes.func.isRequired,
  renderExtraFilters: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  currentPageToFirst: PropTypes.bool,
  setCurrentPageToFirst: PropTypes.func,
  createNewAction: PropTypes.func,
  statusCheck: PropTypes.bool,
  statusKey: PropTypes.string,
  extraData: PropTypes.object,
};

BaseTable.defaultProps = {
  currentPageToFirst: false,
  setCurrentPageToFirst: () => {},
  createNewAction: null,
  statusKey: null,
  statusCheck: false,
  extraData: {},
};

export default withTranslation()(BaseTable);
